/**
 * Pure helpers for appointment slot scheduling: timezone-aware keys, picker disable rules,
 * and bookable date ranges derived from resource policy.
 */

import dayjs, { type Dayjs } from 'dayjs';
import type { AvailableSlot, ResourcePolicy } from '@/api/mcp-appointments';

/** Normalizes an ISO timestamp string to a canonical UTC ISO string for stable comparisons. */
export function normalizeIso(iso: string): string {
  return dayjs(iso).toISOString();
}

/**
 * Returns the calendar date for an instant in a given IANA timezone.
 * @param instant - Moment to interpret.
 * @param timezone - IANA timezone used for the calendar day boundary.
 * @returns Date key in `YYYY-MM-DD` format.
 */
export function getDateKeyInTimezone(instant: Dayjs, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(instant.toDate());
}

/**
 * Returns a month key from a Dayjs value in the local calendar of that value.
 * @returns Month key in `YYYY-MM` format, or `null` when the instant is invalid.
 */
export function getMonthKeyFromDayjs(instant: Dayjs): string | null {
  if (!instant.isValid()) {
    return null;
  }

  return instant.format('YYYY-MM');
}

/**
 * Returns the civil calendar date from an Ant Design picker cell (browser-local Y-M-D).
 * Use for day lookups; do not use on slot instants indexed in a resource timezone.
 */
export function getCalendarDateKey(day: Dayjs): string | null {
  if (!day.isValid()) {
    return null;
  }

  return day.format('YYYY-MM-DD');
}

/**
 * Returns the civil calendar month from an Ant Design picker value (browser-local Y-M).
 * Use for month panel comparisons; do not use on slot instants indexed in a resource timezone.
 */
export function getCalendarMonthKey(day: Dayjs): string | null {
  if (!day.isValid()) {
    return null;
  }

  return day.format('YYYY-MM');
}

/**
 * Returns the calendar month for an instant in a given IANA timezone.
 * @returns Month key in `YYYY-MM` format, or `null` when the instant is invalid.
 */
export function getMonthKeyInTimezone(instant: Dayjs, timezone: string): string | null {
  if (!instant.isValid()) {
    return null;
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit'
  }).formatToParts(instant.toDate());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;

  if (!year || !month) {
    return null;
  }

  return `${year}-${month}`;
}

/** Inputs used when deciding which hours and minutes a date picker should disable for one day. */
export interface BuildDisabledTimeOptions {
  /** Month key (`YYYY-MM`) for which slot data has already been loaded. */
  loadedMonthKey: string;
  /** Minute step the picker enforces when offering times. */
  slotAlignmentMinutes: number;
  /** Whether slot data for the visible month is still being fetched. */
  isFetching: boolean;
  /** Whether any slots were returned for the loaded request. */
  hasSlots: boolean;
}

/** Ant Design time-picker callbacks that disable unavailable hours and minutes. */
export interface DisabledTimeConfig {
  disabledHours: () => number[];
  disabledMinutes: (selectedHour: number) => number[];
}

function buildDisableAllTimesConfig(): DisabledTimeConfig {
  const allHours = Array.from({ length: 24 }, (_, hour) => hour);
  const allMinutes = Array.from({ length: 60 }, (_, minute) => minute);

  return {
    disabledHours: () => allHours,
    disabledMinutes: () => allMinutes
  };
}

/** Disables every hour and minute; used while data is loading or no slots apply to the day. */
export const DISABLE_ALL_TIMES_CONFIG = buildDisableAllTimesConfig();

/**
 * Indexes available slot start times by calendar date in the resource timezone.
 * @returns Map from date key (`YYYY-MM-DD`) to allowed local `hour:minute` strings.
 */
export function buildSlotsLocalTimesByDateKey(
  slots: AvailableSlot[] | undefined,
  timezone: string
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  if (!slots?.length) {
    return map;
  }

  for (const slot of slots) {
    const start = dayjs(slot.start);
    if (!start.isValid()) {
      continue;
    }

    const dateKey = getDateKeyInTimezone(start, timezone);
    const { hour, minute } = getHourMinuteInTimezone(start, timezone);
    const localTimeKey = `${hour}:${minute}`;
    const allowedTimes = map.get(dateKey);
    if (allowedTimes) {
      allowedTimes.add(localTimeKey);
      continue;
    }

    map.set(dateKey, new Set([localTimeKey]));
  }

  return map;
}

/**
 * Builds picker disable rules from the set of allowed local start times on one day.
 * Hours with no aligned slot are fully disabled; remaining hours disable only invalid minutes.
 */
export function buildDisabledTimeFromAllowedTimes(
  allowedLocalTimes: Set<string>,
  slotAlignmentMinutes: number
): DisabledTimeConfig {
  const disabledHoursSet = new Set<number>();
  const disabledMinutesMap = new Map<number, Set<number>>();

  for (let hour = 0; hour < 24; hour++) {
    let hasAvailableSlot = false;

    for (let minute = 0; minute < 60; minute += slotAlignmentMinutes) {
      if (allowedLocalTimes.has(`${hour}:${minute}`)) {
        hasAvailableSlot = true;
        break;
      }
    }

    if (!hasAvailableSlot) {
      disabledHoursSet.add(hour);
    }
  }

  for (let hour = 0; hour < 24; hour++) {
    if (disabledHoursSet.has(hour)) {
      continue;
    }

    const disabledMinutes = new Set<number>();
    for (let minute = 0; minute < 60; minute++) {
      if (!allowedLocalTimes.has(`${hour}:${minute}`)) {
        disabledMinutes.add(minute);
      }
    }

    if (disabledMinutes.size > 0) {
      disabledMinutesMap.set(hour, disabledMinutes);
    }
  }

  return {
    disabledHours: () => Array.from(disabledHoursSet),
    disabledMinutes: (selectedHour: number) => {
      const disabledMinutes = disabledMinutesMap.get(selectedHour);
      return disabledMinutes ? Array.from(disabledMinutes) : [];
    }
  };
}

/**
 * Resolves which hours and minutes should be disabled for a selected calendar day.
 * Falls back to disabling all times when data is missing, stale, or the day has no slots.
 */
export function buildDisabledTimeForDate(
  selectedDate: Dayjs,
  slots: AvailableSlot[] | undefined,
  timezone: string,
  options: BuildDisabledTimeOptions
): DisabledTimeConfig {
  if (!selectedDate.isValid() || !options.hasSlots || options.isFetching) {
    return DISABLE_ALL_TIMES_CONFIG;
  }

  const selectedMonthKey = getCalendarMonthKey(selectedDate);
  if (!selectedMonthKey || selectedMonthKey !== options.loadedMonthKey) {
    return DISABLE_ALL_TIMES_CONFIG;
  }

  const dateKey = getCalendarDateKey(selectedDate);
  if (!dateKey) {
    return DISABLE_ALL_TIMES_CONFIG;
  }

  const allowedLocalTimes = new Set<string>();

  for (const slot of slots ?? []) {
    const start = dayjs(slot.start);
    if (!start.isValid() || getDateKeyInTimezone(start, timezone) !== dateKey) {
      continue;
    }

    const { hour, minute } = getHourMinuteInTimezone(start, timezone);
    allowedLocalTimes.add(`${hour}:${minute}`);
  }

  if (allowedLocalTimes.size === 0) {
    return DISABLE_ALL_TIMES_CONFIG;
  }

  return buildDisabledTimeFromAllowedTimes(allowedLocalTimes, options.slotAlignmentMinutes);
}

/**
 * Extracts hour and minute for an instant in a given IANA timezone.
 * @returns 24-hour clock components as seen in that timezone.
 */
export function getHourMinuteInTimezone(instant: Dayjs, timezone: string): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: false
  }).formatToParts(instant.toDate());
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0);
  return { hour, minute };
}

/** Returns the earlier of two Dayjs values. */
export function minDayjs(a: Dayjs, b: Dayjs): Dayjs {
  return a.isBefore(b) ? a : b;
}

/** Returns the later of two Dayjs values. */
export function maxDayjs(a: Dayjs, b: Dayjs): Dayjs {
  return a.isAfter(b) ? a : b;
}

/**
 * Compares two appointment slot intervals using normalized ISO timestamps.
 * @returns `true` when both start and end instants match after normalization.
 */
export function isSameSlot(
  start: Dayjs,
  end: Dayjs,
  candidateStart: Dayjs,
  candidateEnd: Dayjs
): boolean {
  if (
    !start.isValid()
    || !end.isValid()
    || !candidateStart.isValid()
    || !candidateEnd.isValid()
  ) {
    return false;
  }

  return (
    normalizeIso(start.toISOString()) === normalizeIso(candidateStart.toISOString())
    && normalizeIso(end.toISOString()) === normalizeIso(candidateEnd.toISOString())
  );
}

/**
 * Clips a requested range to what the resource policy allows booking from today onward.
 * @returns ISO `startTime` and `endTime` for slot queries, or `null` when nothing is bookable.
 */
export function buildBookableRange(
  rangeStart: Dayjs,
  rangeEnd: Dayjs,
  resourcePolicy: ResourcePolicy | undefined
): { startTime: string; endTime: string } | null {
  const now = dayjs();
  let start = maxDayjs(rangeStart, now.startOf('day'));

  if (resourcePolicy?.effectiveFrom) {
    start = maxDayjs(start, dayjs(resourcePolicy.effectiveFrom));
  }

  let end = rangeEnd;

  if (resourcePolicy?.maxLeadTimeDays && resourcePolicy.maxLeadTimeDays > 0) {
    end = minDayjs(end, now.add(resourcePolicy.maxLeadTimeDays, 'day'));
  }

  if (resourcePolicy?.effectiveTo) {
    end = minDayjs(end, dayjs(resourcePolicy.effectiveTo));
  }

  if (!end.isAfter(start)) {
    return null;
  }

  return {
    startTime: start.toISOString(),
    endTime: end.toISOString()
  };
}

/**
 * Returns the last calendar day the resource policy allows booking on, from `maxLeadTimeDays`
 * and `effectiveTo`. Day-granular counterpart of the end clipping in `buildBookableRange`.
 * @returns Start of that day, or `null` when the policy sets no upper bound.
 */
export function getLatestBookableDay(resourcePolicy: ResourcePolicy | undefined): Dayjs | null {
  const today = dayjs().startOf('day');
  let maxDay: Dayjs | null = null;

  if (resourcePolicy?.maxLeadTimeDays && resourcePolicy.maxLeadTimeDays > 0) {
    maxDay = today.add(resourcePolicy.maxLeadTimeDays, 'day');
  }

  if (resourcePolicy?.effectiveTo) {
    const effectiveTo = dayjs(resourcePolicy.effectiveTo).startOf('day');
    maxDay = maxDay ? minDayjs(maxDay, effectiveTo) : effectiveTo;
  }

  return maxDay;
}

/**
 * Returns the first calendar day in a month that has at least one available slot.
 * @returns Start of that day in local Dayjs, or `null` when the month has no indexed slots.
 */
export function findFirstDayWithSlotsInMonth(
  month: Dayjs,
  slotsByDateKey: Map<string, AvailableSlot[]> | undefined,
  _timezone: string
): Dayjs | null {
  if (!month.isValid() || !slotsByDateKey?.size) {
    return null;
  }

  const targetMonthKey = getCalendarMonthKey(month);
  if (!targetMonthKey) {
    return null;
  }

  const firstDateKey = Array.from(slotsByDateKey.keys())
    .filter((dateKey) => {
      const monthFromDateKey = dateKey.slice(0, 7);
      return monthFromDateKey === targetMonthKey
        && (slotsByDateKey.get(dateKey)?.length ?? 0) > 0;
    })
    .sort()[0];

  if (!firstDateKey) {
    return null;
  }

  const firstSlotStart = slotsByDateKey.get(firstDateKey)?.[0]?.start;
  if (firstSlotStart) {
    const firstSlotDay = dayjs(firstSlotStart);
    if (firstSlotDay.isValid()) {
      return firstSlotDay.startOf('day');
    }
  }

  return dayjs(firstDateKey);
}

/**
 * Finds the earliest available slot start on a preferred calendar day.
 * @param preferredDate - Day the user selected or was steered toward.
 * @param slotsByDateKey - Optional pre-indexed slots; avoids scanning the full list when present.
 * @returns First slot start on that day, or `null` when none exist.
 */
export function findFirstSlotStartOnDay(
  preferredDate: Dayjs,
  slots: AvailableSlot[] | undefined,
  timezone: string,
  slotsByDateKey?: Map<string, AvailableSlot[]>
): Dayjs | null {
  if (!preferredDate.isValid() || !slots?.length) {
    return null;
  }

  const preferredDateKey = getCalendarDateKey(preferredDate);
  if (!preferredDateKey) {
    return null;
  }
  const indexedSlots = slotsByDateKey?.get(preferredDateKey);
  if (indexedSlots?.length) {
    return dayjs(indexedSlots[0].start);
  }

  const matchingStarts = slots
    .map((slot) => dayjs(slot.start))
    .filter((start) => start.isValid() && getDateKeyInTimezone(start, timezone) === preferredDateKey)
    .sort((a, b) => a.valueOf() - b.valueOf());

  return matchingStarts[0] ?? null;
}

/**
 * Groups available slots by calendar date in the resource timezone.
 * @returns Map from date key (`YYYY-MM-DD`) to slots on that day, sorted by start time.
 */
export function buildSlotsByDateKey(
  slots: AvailableSlot[] | undefined,
  timezone: string
): Map<string, AvailableSlot[]> {
  const map = new Map<string, AvailableSlot[]>();
  if (!slots?.length) {
    return map;
  }

  for (const slot of slots) {
    const start = dayjs(slot.start);
    if (!start.isValid()) {
      continue;
    }

    const dateKey = getDateKeyInTimezone(start, timezone);
    const daySlots = map.get(dateKey);
    if (daySlots) {
      daySlots.push(slot);
      continue;
    }

    map.set(dateKey, [slot]);
  }

  for (const daySlots of map.values()) {
    daySlots.sort((left, right) => dayjs(left.start).valueOf() - dayjs(right.start).valueOf());
  }

  return map;
}
